<script setup>
import { deburr, clone, uniq } from 'lodash';
import { ref, computed, reactive } from 'vue';
import apiMatriculas from '@/api/cifra/matriculas';
import Tabla from '../tabla/Tabla.vue';
import ModalRemoto from '../ModalRemoto.vue';
import InputCheckboxBoolean from '../cifra/InputCheckboxBoolean.vue';
import CabeceraFiltroSelector from '../tabla/CabeceraFiltroSelector.vue';
import CabeceraFiltroTexto from '../tabla/CabeceraFiltroTexto.vue';
import BotonGuardar from '../inputs/BotonGuardar.vue';

const props = defineProps({
  curso: { type: Object, required: true },
  matriculas: { type: Array, required: true },
  // Por separado nos llegan los grupos de cada id de matrícula
  grupos: { type: Object, required: true },
  optativas: { type: Array, required: true },
});

const guardando = ref(false);
const showModalPreferenciasOptativas = ref(false);
const matriculaCursoSeleccionado = ref(null);
const filtros = ref({
  grupo: null, // Filtro por grupo
});
// hash con el id de matrícula y dentro un array las optativas seleccionadas
const matriculasOptativasSeleccionadas = reactive({});

const matriculasVisibles = computed(() => {
  let matriculas = clone(props.matriculas);
  // Filtramos por grupo
  if (filtros.value.grupo) {
    matriculas = matriculas.filter((matricula) => (
      props.grupos[matricula.id]?.grupo === filtros.value.grupo
    ));
  }

  // Filtramos por modalidad
  if (filtros.value.modalidad) {
    matriculas = matriculas.filter((matricula) => (
      matricula.matriculaCursoActual?.modalidad?.nombre === filtros.value.modalidad
    ));
  }

  // Filtramos por alumno (nombre o parte del nombre)
  if (filtros.value.alumno && filtros.value.alumno.trim() !== '') {
    const filtroAlumno = filtros.value.alumno.trim().toLowerCase();

    matriculas = matriculas.filter((matricula) => {
      const nombreCompleto = deburr(matricula.alumno?.nombre_completo?.toLowerCase() || '');

      const filtroSinTildes = deburr(filtroAlumno);
      return nombreCompleto.includes(filtroSinTildes);
    });
  }

  return matriculas.map((matricula) => {
    matricula.matriculaCursoActual = matricula.cursos?.find(
      (matriculaCurso) => matriculaCurso.curso_id === props.curso.id,
    );
    matricula.grupo = props.grupos[matricula.id]?.grupo;
    return matricula;
  });
});

const listaGrupos = computed(() => (
  uniq(Object.values(props.grupos).map((grupo) => grupo.grupo)).sort()
));

const listaModalidades = computed(() => {
  const modalidades = props.matriculas
    .map((matricula) => matricula.matriculaCursoActual?.modalidad?.nombre)
    .filter((modalidad) => modalidad);

  return uniq(modalidades).sort((a, b) => a.localeCompare(b));
});

const hayMatriculasConModalidades = computed(() => (
  listaModalidades.value.length > 0
));

function cargaInicial() {
  Object.assign(
    matriculasOptativasSeleccionadas,
    matriculasVisibles.value.reduce((acc, matricula) => {
      let asignaturaIds = matricula.matriculaCursoActual.asignatura_ids || [];

      asignaturaIds = asignaturaIds.filter((id) => (
        props.optativas.some((optativa) => optativa.id === id)
      ));

      acc[matricula.id] = asignaturaIds;
      return acc;
    }, {}),
  );
}

cargaInicial();

function abrirPreferenciasOptativas(matriculaCursoId) {
  matriculaCursoSeleccionado.value = matriculaCursoId;
  showModalPreferenciasOptativas.value = true;
}

function optativaSeleccionada(matriculaId, optativaId, seleccionada) {
  const optativasSeleccionadas = matriculasOptativasSeleccionadas[matriculaId];

  if (!seleccionada) {
    optativasSeleccionadas.splice(optativasSeleccionadas.indexOf(optativaId), 1);
  } else {
    optativasSeleccionadas.push(optativaId);
  }
}

/**
 * Marca o desmarca la optativa para todas las matrículas visibles
 */
function marcarTodo(optativa) {
  let algunaSinMarcar = false;

  matriculasVisibles.value.forEach((matricula) => {
    const optativas = matriculasOptativasSeleccionadas[matricula.id] || [];
    if (!optativas.includes(optativa.id)) {
      algunaSinMarcar = true;
      optativas.push(optativa.id);
    }
  });

  matriculasVisibles.value.forEach((matricula) => {
    const optativas = matriculasOptativasSeleccionadas[matricula.id] || [];
    if (!optativas.includes(optativa.id)) {
      algunaSinMarcar = true;
      optativas.push(optativa.id);
    }
  });

  // Es están todas marcadas, las desmarcamos
  if (!algunaSinMarcar) {
    // Si todas estaban marcadas, las desmarcamos
    matriculasVisibles.value.forEach((matricula) => {
      const optativas = matriculasOptativasSeleccionadas[matricula.id] || [];
      if (optativas.includes(optativa.id)) {
        optativas.splice(optativas.indexOf(optativa.id), 1);
      }
    });
  }
}

function guardar() {
  guardando.value = true;

  const datos = {
    curso_id: props.curso.id,
    matriculas: [],
  };

  matriculasVisibles.value.forEach((matricula) => {
    datos.matriculas.push({
      id: matricula.id,
      matricula_curso_id: matricula.matriculaCursoActual.id,
      asignatura_ids: matriculasOptativasSeleccionadas[matricula.id] || [],
    });
  });

  apiMatriculas.guardarOptativas(datos).then(() => {
    // Aquí podríamos mostrar un mensaje de éxito
    console.log('Asignación de optativas guardada correctamente');
  }).catch((error) => {
    // Aquí podríamos manejar el error, por ejemplo, mostrar un mensaje
    console.error('Error al guardar las optativas:', error);
  }).finally(() => {
    guardando.value = false;
  });
}
</script>

<template>
  <div class="asignacion-optativas-view">
    <teleport to="body">
      <ModalRemoto
        id="modal-preferencias-optativas"
        v-model="showModalPreferenciasOptativas"
        :url="`/matriculas/preferencia_optativas/${matriculaCursoSeleccionado}?tipo=matricula`"
        :titulo="$t('preferencia_optativas')"
      >
        <i class="fas fa-books"></i>
      </ModalRemoto>
    </teleport>
    <Tabla
      id="tabla-asignacion-optativas"
      v-model:filtros="filtros"
      class="cifra-table table table-hover table-sortable table-striped"
    >
      <thead>
        <tr>
          <CabeceraFiltroTexto class="head-alumno" campo="alumno"></CabeceraFiltroTexto>
          <th></th>
          <CabeceraFiltroSelector campo="grupo" :valores="listaGrupos">
          </CabeceraFiltroSelector>
          <CabeceraFiltroSelector
            v-if="hayMatriculasConModalidades"
            campo="modalidad"
            :valores="listaModalidades"
          >
          </CabeceraFiltroSelector>
          <th
            v-for="optativa in optativas"
            :key="optativa.id"
            class="text-nowrap"
          >
            <i class="fa fa-check-square clickable m-r-xs" @click="marcarTodo(optativa)"></i>
            <span v-tooltip="optativa.nombre">
              {{ optativa.abreviatura }}
            </span>
            <i
              v-if="optativa.modalidad"
              v-tooltip="optativa.modalidad.nombre"
              class="fa fa-info-circle m-l-xs"
            ></i>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="matricula in matriculasVisibles"
          :key="matricula.id"
        >
          <td class="col-min col-alumno">
            <img
              :src="matricula.alumno.foto_url"
              class="usuario-thumbnail usuario-thumbnail-sm img-circle img-thumbnail"
            />
            {{ matricula.alumno.nombre_completo }}
          </td>
          <td class="col-min va-m">
            <div
              v-if="matricula.matriculaCursoActual?.preferencia_optativas"
              class="pull-right clickable"
            >
              <i
                class="fa fa-books"
                @click="abrirPreferenciasOptativas(matricula.matriculaCursoActual.id)"
              ></i>
            </div>
          </td>
          <td class="col-min">
            {{ matricula.grupo }}
          </td>
          <td v-if="hayMatriculasConModalidades" class="col-min">
            {{ matricula.matriculaCursoActual?.modalidad?.nombre || '' }}
          </td>
          <td v-for="optativa in optativas" :key="optativa.id">
            <InputCheckboxBoolean
              :model-value="matriculasOptativasSeleccionadas[matricula.id]?.includes(optativa.id)"
              @update:model-value="optativaSeleccionada(matricula.id, optativa.id, $event)"
            ></InputCheckboxBoolean>
          </td>
        </tr>
      </tbody>
      <tbody v-if="!matriculasVisibles.length">
        <tr>
          <td :colspan="3 + optativas.length">
            {{ $t('no_resultado_busqueda') }}
          </td>
        </tr>
      </tbody>
    </Tabla>
    <div class="text-center m-t p-b-lg">
      <BotonGuardar
        :submitting="guardando"
        @click="guardar"
      >
      </BotonGuardar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/../assets/stylesheets/cifra/vars/main.scss";

#tabla-asignacion-optativas {
  margin-top: 0;
  // Si no se pierde el borde inferior al hacer scroll
  border-collapse: separate;

  thead {
    position: sticky;
    top: 0;
    background: white;
    z-index: 4;

    th {
      user-select: none;
    }
  }

  .col-alumno, .head-alumno {
    position: sticky;
    left: 0;
    background: white;
    z-index: 2;
  }
}
</style>
